/**
 * Onboarding step 8 — Activate. The one satisfying click that performs
 * every write (Brain facts with founder provenance, persona records,
 * queued invites, the first seeded task, the completion flag). All
 * deterministic — no model calls; the write path is
 * `activateOnboarding` in src/lib/onboarding.ts.
 */

import { CheckCircle2, Rocket } from 'lucide-react';
import type { ActivationResult } from '../../../lib/onboarding';

interface Props {
  companyName: string;
  busy: boolean;
  result: ActivationResult | null;
  onActivate: () => void;
  onDone: () => void;
}

export function StepActivate({ companyName, busy, result, onActivate, onDone }: Props) {
  if (result) {
    return (
      <div className="onb-step onb-activate">
        <CheckCircle2 size={44} className="onb-activate__icon onb-activate__icon--done" />
        <h3 className="t-section-title onb-step__title">
          {companyName || 'Your workspace'} is live.
        </h3>
        <ul className="onb-activate__summary">
          <li>
            {result.factsWritten} founder fact{result.factsWritten === 1 ? '' : 's'} written
            to the Company Brain
          </li>
          <li>
            {result.personasCreated} persona{result.personasCreated === 1 ? '' : 's'} created
          </li>
          <li>
            {result.invitesQueued} team invite{result.invitesQueued === 1 ? '' : 's'} queued
          </li>
          <li>{result.taskCreated ? 'First task seeded on your board' : 'No task seeded'}</li>
        </ul>
        <button
          type="button"
          className="glass-btn glass-btn--primary onb-activate__btn"
          onClick={onDone}
        >
          Enter Argo OS
        </button>
      </div>
    );
  }

  return (
    <div className="onb-step onb-activate">
      <Rocket size={44} className="onb-activate__icon" />
      <h3 className="t-section-title onb-step__title">Ready to activate</h3>
      <p className="t-meta onb-step__blurb" style={{ maxWidth: 420, margin: '0 auto 18px' }}>
        One click writes everything you entered into the Company Brain as
        founder-stated facts, creates your personas, queues your invites, and
        seeds your first task. Deterministic — no AI involved.
      </p>
      <button
        type="button"
        className="glass-btn glass-btn--primary onb-activate__btn"
        onClick={onActivate}
        disabled={busy}
      >
        <Rocket size={15} /> {busy ? 'Activating…' : 'Activate my workspace'}
      </button>
    </div>
  );
}
